import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, UtensilsCrossed, Truck, ShoppingBag, Clock, AlertTriangle, Building } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCart } from "@/context/CartContext";
import axios from "axios";
import API from "@/utils/api";

export default function OrderTypePage() {
  const navigate = useNavigate();
  const { getTotal, getItemCount } = useCart();
  const [orderType, setOrderType] = useState(null);
  const [tableNumber, setTableNumber] = useState("");
  const [deliveryFee, setDeliveryFee] = useState(2.50);
  const [restaurantStatus, setRestaurantStatus] = useState({ is_open: true, message: "" });
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [features, setFeatures] = useState({});
  
  // Branch selection
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isLoadingBranches, setIsLoadingBranches] = useState(true);

  // Fetch delivery fee from config and branches
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [configRes, branchesRes] = await Promise.all([
          axios.get(`${API}/config`),
          axios.get(`${API}/public/branches`)
        ]);
        
        if (configRes.data?.delivery_fee) {
          setDeliveryFee(configRes.data.delivery_fee);
        }
        if (configRes.data?.features) {
          setFeatures(configRes.data.features);
        }
        
        // Handle branches (API already filters by active)
        setBranches(branchesRes.data);
        
        console.log("🔍 [OrderTypePage] Sucursales cargadas:", branchesRes.data.length);
        console.log("📍 [OrderTypePage] Sucursales:", branchesRes.data);
        
        // Auto-select if only one branch
        if (branchesRes.data.length === 1) {
          setSelectedBranch(branchesRes.data[0]);
          console.log("✅ [OrderTypePage] Auto-seleccionada única sucursal:", branchesRes.data[0].name);
        }
      } catch (error) {
        console.error("❌ [OrderTypePage] Error fetching data:", error);
      } finally {
        setIsLoadingBranches(false);
      }
    };
    fetchData();
  }, []);

  // Check restaurant status when branch is selected
  useEffect(() => {
    const checkStatus = async () => {
      if (!selectedBranch) {
        // No branch selected yet, check general status
        try {
          const statusRes = await axios.get(`${API}/restaurant/status`);
          setRestaurantStatus(statusRes.data);
          setIsLoadingStatus(false);
        } catch (error) {
          console.error("❌ [OrderTypePage] Error fetching status:", error);
          setIsLoadingStatus(false);
        }
        return;
      }
      
      // Branch selected, check branch-specific status
      try {
        const statusRes = await axios.get(`${API}/restaurant/status?branch_id=${selectedBranch.branch_id}`);
        console.log("🏪 [OrderTypePage] Status for branch:", selectedBranch.name, statusRes.data);
        setRestaurantStatus(statusRes.data);
        setIsLoadingStatus(false);
      } catch (error) {
        console.error("❌ [OrderTypePage] Error fetching branch status:", error);
        setIsLoadingStatus(false);
      }
    };
    
    checkStatus();
  }, [selectedBranch]);

  const handleContinue = () => {
    if (!orderType) return;
    
    // Validate branch selection (if multiple branches)
    if (branches.length > 1 && !selectedBranch) {
      return;
    }

    // Store order type and branch in sessionStorage for checkout
    sessionStorage.setItem("orderType", orderType);
    
    if (selectedBranch) {
      sessionStorage.setItem("selectedBranch", JSON.stringify(selectedBranch));
    }
    
    if (orderType === "local" && tableNumber) {
      sessionStorage.setItem("tableNumber", tableNumber);
    } else {
      sessionStorage.removeItem("tableNumber");
    }

    navigate("/checkout");
  };

  if (getItemCount() === 0) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Tu carrito está vacío</p>
          <Button
            className="bg-primary text-black hover:opacity-90"
            style={{ backgroundColor: 'var(--primary)' }}
            onClick={() => navigate("/menu")}
          >
            Ver menú
          </Button>
        </div>
      </div>
    );
  }

  // Show restaurant closed message
  if (!isLoadingStatus && !restaurantStatus.is_open) {
    return (
      <div className="min-h-screen bg-black">
        {/* Header */}
        <div className="sticky top-0 bg-black/95 backdrop-blur z-30 px-4 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/cart")}
              className="text-gray-400 hover:text-primary"
              data-testid="ordertype-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-bold text-primary" style={{ color: 'var(--primary)' }}>
              RESTAURANTE CERRADO
            </h1>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center p-8 mt-12">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mb-6">
            <Clock className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Lo sentimos</h2>
          <p className="text-gray-400 text-center mb-4" data-testid="restaurant-closed-message">
            {restaurantStatus.message || "El restaurante está cerrado ahora mismo"}
          </p>
          {restaurantStatus.schedule && restaurantStatus.schedule.enabled && (
            <p className="text-yellow-500 text-sm">
              Horario de hoy: {restaurantStatus.schedule.open} - {restaurantStatus.schedule.close}
            </p>
          )}
          <div className="mt-8 space-y-3 w-full max-w-xs">
            <Button
              className="w-full bg-primary text-black hover:opacity-90"
              style={{ backgroundColor: 'var(--primary)' }}
              onClick={() => navigate("/menu")}
            >
              Explorar el menú
            </Button>
            <Button
              variant="outline"
              className="w-full border-gray-700 text-gray-300"
              onClick={() => navigate("/")}
            >
              Volver al inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="sticky top-0 bg-black/95 backdrop-blur z-30 px-4 py-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/cart")}
            className="text-gray-400 hover:text-primary"
            data-testid="ordertype-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-bold text-primary" style={{ color: 'var(--primary)' }}>
            ¿DÓNDE QUIERES TU PEDIDO?
          </h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Branch Selector (only if > 1 branch) */}
        {console.log("🔍 [OrderTypePage RENDER] branches.length:", branches.length)}
        {branches.length > 1 && (
          <div className="space-y-3">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2 text-white">
              <Building className="w-5 h-5 text-primary" style={{ color: 'var(--primary)' }} />
              SELECCIONA SUCURSAL
            </h2>
            {branches.map((branch) => (
              <button
                key={branch.branch_id}
                onClick={() => setSelectedBranch(branch)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  selectedBranch?.branch_id === branch.branch_id
                    ? "border-primary bg-primary/10"
                    : "border-gray-700 bg-gray-900 hover:border-gray-600"
                }`}
                style={selectedBranch?.branch_id === branch.branch_id ? { borderColor: 'var(--primary)', backgroundColor: 'rgba(var(--primary-rgb), 0.1)' } : {}}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">{branch.name}</p>
                    <p className="text-sm text-gray-400">{branch.address}</p>
                  </div>
                  {selectedBranch?.branch_id === branch.branch_id && (
                    <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center" style={{ backgroundColor: 'var(--primary)' }}>
                      <div className="w-2 h-2 bg-black rounded-full" />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Show warning if no branch selected */}
        {branches.length > 1 && !selectedBranch && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-500 text-sm">
                Por favor selecciona una sucursal para continuar
              </p>
            </div>
          </div>
        )}

        {/* Order Type Selection */}
        <div className="space-y-3">

          {/* PICKUP Option - Recoger en tienda */}
          <button
            onClick={() => setOrderType("pickup")}
            data-testid="order-type-pickup"
            className={`w-full p-5 rounded-xl border-2 text-left transition-all ${orderType === "pickup"
              ? "border-primary bg-primary/10"
              : "border-gray-700 bg-gray-900 hover:border-gray-600"
              }`}
            style={orderType === "pickup" ? { borderColor: 'var(--primary)', backgroundColor: 'rgba(var(--primary-rgb), 0.1)' } : {}}
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${orderType === "pickup" ? "bg-primary" : "bg-gray-800"
                }`} style={orderType === "pickup" ? { backgroundColor: 'var(--primary)' } : {}}>
                <ShoppingBag className={`w-6 h-6 ${orderType === "pickup" ? "text-black" : "text-primary"
                  }`} style={orderType !== "pickup" ? { color: 'var(--primary)' } : {}} />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-lg text-white">
                  RECOGER EN TIENDA
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  Pido desde casa y recojo en el local cuando esté listo
                </p>
                <p className="text-primary font-bold mt-2" style={{ color: 'var(--primary)' }}>Sin gastos de envío</p>
              </div>
            </div>
          </button>

          {/* LOCAL Option - Comer en el local */}
          <button
            onClick={() => setOrderType("local")}
            data-testid="order-type-local"
            className={`w-full p-5 rounded-xl border-2 text-left transition-all ${orderType === "local"
              ? "border-green-500 bg-green-500/10"
              : "border-gray-700 bg-gray-900 hover:border-gray-600"
              }`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${orderType === "local" ? "bg-green-500" : "bg-gray-800"
                }`}>
                <UtensilsCrossed className={`w-6 h-6 ${orderType === "local" ? "text-black" : "text-green-500"
                  }`} />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-lg text-white">
                  COMER EN EL LOCAL
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  Ya estoy aquí, pido y me lo llevan a la mesa
                </p>
                <p className="text-green-500 font-bold mt-2">Sin gastos de envío</p>
              </div>
            </div>
          </button>

          {/* Table Number Input (only shown when LOCAL is selected) */}
          {orderType === "local" && (
            <div className="bg-gray-900 rounded-xl p-4 border border-gray-800 animate-fade-in">
              <Label className="text-gray-300">Número de mesa (opcional)</Label>
              <Input
                value={tableNumber}
                onChange={(e) => setTableNumber(e.target.value)}
                placeholder="Ej: Mesa 5"
                className="mt-2 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-primary/50"
                style={{ '--tw-ring-color': 'var(--primary)' }}
                data-testid="table-number-input"
              />
              <p className="text-gray-500 text-xs mt-2">
                Si estás en una mesa, indícanos el número y te llevaremos el pedido
              </p>
            </div>
          )}

          {/* DELIVERY Option */}
          {features?.delivery !== false && (
            <button
              onClick={() => setOrderType("delivery")}
              data-testid="order-type-delivery"
              className={`w-full p-5 rounded-xl border-2 text-left transition-all ${orderType === "delivery"
                ? "border-blue-500 bg-blue-500/10"
                : "border-gray-700 bg-gray-900 hover:border-gray-600"
                }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${orderType === "delivery" ? "bg-blue-500" : "bg-gray-800"
                  }`}>
                  <Truck className={`w-6 h-6 ${orderType === "delivery" ? "text-white" : "text-blue-500"
                    }`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-lg text-white">
                    ENVÍO A DOMICILIO
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">
                    Te llevamos tu pedido a la dirección que elijas
                  </p>
                  <p className="text-blue-500 font-bold mt-2">+ {deliveryFee.toFixed(2).replace('.', ',')} € de envío</p>
                </div>
              </div>
            </button>
          )}
        </div>

        {/* Order Summary */}
        <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Subtotal del carrito</span>
            <span className="text-white font-bold text-lg">{getTotal().toFixed(2)} €</span>
          </div>
        </div>

        {/* Continue Button */}
        <Button
          className="w-full h-14 rounded-full font-bold text-lg bg-primary hover:opacity-90 text-black disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: 'var(--primary)' }}
          onClick={handleContinue}
          disabled={!orderType || (branches.length > 1 && !selectedBranch)}
          data-testid="continue-to-checkout"
        >
          {!orderType ? "Selecciona una opción" : (branches.length > 1 && !selectedBranch) ? "Selecciona una sucursal" : "Continuar"}
        </Button>
      </div>
    </div>
  );
}
